/**
 * CetusEd Community Portal — Previously Uploaded Files
 *
 * COMPLIANCE: IDEA §300.304 — Parents may provide evaluation data for
 *   consideration. Previously uploaded files are listed with their upload
 *   date and category. Files remain available as part of the student's
 *   education record under FERPA §1232g.
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useUploadedFiles } from '../../api/hooks';
import { Card } from '../../ui/components/Card';
import { Button } from '../../ui/components/Button';
import { LoadingSpinner } from '../../ui/components/LoadingSpinner';

export const FileList: React.FC = () => {
  const auth = useAuth();
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    auth.user?.linkedStudents[0]?.id || null,
  );

  const { data: files, isLoading, error } = useUploadedFiles(selectedStudentId || '');

  const handleStudentChange = useCallback((studentId: string) => {
    setSelectedStudentId(studentId);
  }, []);

  if (!auth.user) return null;

  return (
    <div className="file-list-page">
      <div className="page-header">
        <h1>My Uploaded Files</h1>
        <Link to="/files/upload">
          <Button>Upload New</Button>
        </Link>
      </div>

      {auth.user.linkedStudents.length > 1 && (
        <div className="student-selector">
          <label htmlFor="fl-student-select">Select Student:</label>
          <select
            id="fl-student-select"
            value={selectedStudentId || ''}
            onChange={(e) => handleStudentChange(e.target.value)}
          >
            {auth.user.linkedStudents.map((student) => (
              <option key={student.id} value={student.id}>
                {student.name} — {student.school}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoading && <LoadingSpinner />}

      {error && (
        <div className="error-message" role="alert">
          Unable to load uploaded files. Please try again later.
        </div>
      )}

      {files && files.length === 0 && (
        <p className="empty-state">
          No files uploaded yet. You can upload documents to support your student's evaluation or IEP process.
        </p>
      )}

      {files && files.length > 0 && (
        <div className="file-grid">
          {files.map((file) => (
            <Card key={file.id}>
              <div className="file-card-content">
                <h3>{file.filename}</h3>
                <p className="file-meta">
                  <span className="file-category">{file.category}</span>
                  <span className="file-size">
                    {(file.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                  <span className="file-date">
                    {new Date(file.uploadedAt).toLocaleDateString()}
                  </span>
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
